import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'
import type { MemberPortalOrganization } from './MemberSignIn'

type OrganizationProfile = {
  name: string
  profile_photo?: { thumbnail_url: string }
  directory_listing: { appears: boolean; opt_in: boolean }
  custom_fields: Array<{
    id: number
    label: string
    value: string | boolean
    field_access: 'member_edit' | 'view_only'
  }>
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

function ProfileIdentity({
  current,
  editing,
  name,
  setName,
}: {
  current: OrganizationProfile
  editing: boolean
  name: string
  setName: (value: string) => void
}) {
  return (
    <div className="mt-3 flex items-center gap-3">
      {current.profile_photo ? (
        <img
          src={current.profile_photo.thumbnail_url}
          alt=""
          className="size-14 rounded-md object-cover"
        />
      ) : (
        <div className="flex size-14 items-center justify-center rounded-md bg-muted text-heading-sm font-medium text-muted-foreground">
          {initials(current.name)}
        </div>
      )}
      {editing ? (
        <Input
          aria-label="Organization name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      ) : (
        <p className="text-body-sm font-medium">{current.name}</p>
      )}
    </div>
  )
}

function CustomFieldRow({
  field,
  editing,
  customFields,
  setCustomFields,
}: {
  field: OrganizationProfile['custom_fields'][number]
  editing: boolean
  customFields: Record<string, string | boolean>
  setCustomFields: React.Dispatch<React.SetStateAction<Record<string, string | boolean>>>
}) {
  return (
    <div className="mt-3 flex gap-4 border-t border-border pt-3">
      <span className="w-40 shrink-0 text-caption text-muted-foreground">{field.label}</span>
      {editing && field.field_access === 'member_edit' ? (
        <Input
          aria-label={field.label}
          value={String(customFields[String(field.id)] ?? '')}
          onChange={(event) =>
            setCustomFields((values) => ({ ...values, [field.id]: event.target.value }))
          }
        />
      ) : (
        <span className="text-body-sm">
          {String(editing ? (customFields[String(field.id)] ?? field.value) : field.value)}
        </span>
      )}
    </div>
  )
}

export function ProfilePanel({ organization }: { organization: MemberPortalOrganization }) {
  const queryClient = useQueryClient()
  const key = ['member-portal-organization-profile', organization.id]
  const profile = useQuery<{ organization_profile: OrganizationProfile }>({
    queryKey: key,
    queryFn: () => getJSON(`/api/v1/member_portal/organizations/${organization.id}/profile`),
    enabled: organization.profile === true,
  })
  const [editing, setEditing] = React.useState(false)
  const [name, setName] = React.useState('')
  const [optIn, setOptIn] = React.useState(false)
  const [customFields, setCustomFields] = React.useState<Record<string, string | boolean>>({})
  const update = useMutation({
    mutationFn: () =>
      patchJSON(`/api/v1/member_portal/organizations/${organization.id}/profile`, {
        organization_profile: {
          name,
          public_directory_opt_in: optIn,
          custom_field_values: customFields,
        },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data)
      setEditing(false)
    },
  })
  const current = profile.data?.organization_profile
  if (profile.isLoading)
    return (
      <p className="text-caption text-muted-foreground" role="status">
        Loading profile…
      </p>
    )
  if (!current)
    return (
      <p className="text-caption text-destructive" role="alert">
        Unable to load profile.
      </p>
    )
  const beginEditing = () => {
    setName(current.name)
    setOptIn(current.directory_listing.opt_in)
    setCustomFields(
      Object.fromEntries(
        current.custom_fields
          .filter((field) => field.field_access === 'member_edit')
          .map((field) => [String(field.id), field.value]),
      ),
    )
    setEditing(true)
  }
  return (
    <section className="border-t border-border px-4 py-4 sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-body font-medium">Profile</h2>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Edit profile"
            className="size-9 px-0"
            onClick={beginEditing}
          >
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <ProfileIdentity current={current} editing={editing} name={name} setName={setName} />
      <div className="mt-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-body-sm font-medium">Directory listing</p>
          <p className="text-caption text-muted-foreground">
            Show this organization in the public directory.
          </p>
        </div>
        <Switch
          checked={editing ? optIn : current.directory_listing.opt_in}
          disabled={!editing || update.isPending}
          onCheckedChange={setOptIn}
          aria-label="Directory listing"
        />
      </div>
      {current.custom_fields.map((field) => (
        <CustomFieldRow
          key={field.id}
          field={field}
          editing={editing}
          customFields={customFields}
          setCustomFields={setCustomFields}
        />
      ))}
      {update.isError && (
        <p className="mt-3 text-caption text-destructive" role="alert">
          Unable to save profile.
        </p>
      )}
      {editing && (
        <div className="mt-4 flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => update.mutate()}
            disabled={update.isPending}
          >
            Save
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </section>
  )
}
